<template>
  <div class="progress-preview-summary">
    <br /><br />

    <ScheduleTracker>
      <ScheduleTrackerItem
        v-for="(taskItem, index) of task.taskItems"
        :key="taskItem.id"
        :index="index"
        :current="isTaskItemActive(taskItem)"
        :completed="isTaskItemCompleted(taskItem)"
        :cancelled="isTaskItemCancelled(taskItem)"
      >
        <ItemPanel :icon="taskItemIcon(taskItem)" class="task-progress-panel">
          <template v-slot:title>
            <strong>
              {{taskItem.name}}
            </strong>
          </template>
          <template v-slot:description>
            {{formatLookupName(taskItem.type, TaskItemTypeLookup)}}
          </template>

          <template v-slot:content>
            <div class="row">
              <div class="col-4">
                <ItemPanelData title="Type">
                  <LabelBadge :labelClass="`label-taskitem-type-${taskItem.type}`">{{taskItem.type | formatLookupName(TaskItemTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Status">
                  <LabelBadge :labelClass="`label-taskitem-status-${taskItem.status}`">{{taskItem.status | formatLookupName(TaskItemStatusTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Address" v-if="isTaskItemLocationEnabled(taskItem)">
                  <LinkGoogleMap :fullAddress="taskItem.locationAddress"></LinkGoogleMap>
                </ItemPanelData>
                <ItemPanelData title="Details" v-if="taskItem.details">
                  {{taskItem.details}}
                </ItemPanelData>
                <ItemPanelData title="Stoppage Labels" v-if="isTaskItemStoppageType(taskItem) && hasLabels(taskItem.stoppageLabels)">
                  <LabelTags :labelList="taskItem.stoppageLabels" />
                </ItemPanelData>
              </div>
              <div class="col-4">
                <ItemPanelData title="Started" v-if="taskItem.startedDate">
                  {{formatShortDateTime(taskItem.startedDate)}}
                </ItemPanelData>
                <ItemPanelData title="Est. Arrival" v-if="isTaskItemLocationEnabled(taskItem)">
                  {{formatShortDateTime(taskItem.estimatedArrivalDate)}}
                </ItemPanelData>
                <ItemPanelData title="Arrival Time" v-if="isTaskItemLocationEnabled(taskItem) && taskItem.isResourceArrived">
                  {{formatShortDateTime(taskItem.arrivalDate)}}
                </ItemPanelData>
                <ItemPanelData title="Est. Completion" v-if="taskItem.estimatedCompletionDate">
                  {{formatShortDateTime(taskItem.estimatedCompletionDate)}}
                </ItemPanelData>                  
                <ItemPanelData title="Completed" v-if="isTaskItemCompleted(taskItem)">
                  {{formatShortDateTime(taskItem.completedDate)}}
                </ItemPanelData>
                <ItemPanelData title="Est. Leaving" v-if="isTaskItemLocationEnabled(taskItem) && taskItem.estimatedLeaveDate">
                  {{formatShortDateTime(taskItem.estimatedLeaveDate)}}
                </ItemPanelData>
                <ItemPanelData title="Left At" v-if="isTaskItemLocationEnabled(taskItem) && isTaskItemCompleted(taskItem) && taskItem.leaveDate">
                  {{formatShortDateTime(taskItem.leaveDate)}}
                </ItemPanelData>
              </div>
              <div class="col-4">
                <div v-if="taskItemRequiresLoading(taskItem)">
                  <ItemPanelData title="Scanned In">
                    {{taskItemTotalScannedInQty(taskItem, taskItemScannedItemList)}}
                  </ItemPanelData>
                  <ItemPanelData title="Scanned Out">
                    {{taskItemTotalScannedOutQty(taskItem, taskItemScannedItemList)}}
                  </ItemPanelData>
                  <ItemPanelData title="Partial Scan Out" v-if="hasLabels(taskItem.partialScanOutLabels)">
                    <LabelTags :labelList="taskItem.partialScanOutLabels" />
                  </ItemPanelData>
                </div>
                <ItemPanelData title="Cancelled" v-if="isTaskItemCancelled(taskItem) && hasLabels(taskItem.itemCancellationLabels)">
                  <LabelTags :labelList="taskItem.itemCancellationLabels" />
                </ItemPanelData>
                <ItemPanelData title="Late Arrival" v-if="taskItem.isLateArrival && hasLabels(taskItem.lateArrivalLabels)">
                  <LabelTags :labelList="taskItem.lateArrivalLabels" />
                </ItemPanelData>
                <ItemPanelData title="Late Arrival Reason" v-if="taskItem.isLateArrival">
                  {{taskItem.lateArrivalReason}}
                </ItemPanelData>
                <ItemPanelData title="Sign Off" v-if="isTaskItemCompleted(taskItem) && isTaskItemOrderDespatchType(taskItem)">
                  {{contactSignoffDescription(taskItem)}}
                </ItemPanelData>
              </div>
            </div>

            <div v-if="isTaskItemLocationEnabled(taskItem) && isTaskItemActive(taskItem) && !isTaskItemStoppageType(taskItem) && taskItem.estimatedArrivalDate">
              <FormHorizontalRule />
              <h4 class="text-center">{{calculateTaskItemProgressDescription(taskItem)}}</h4>
              <div class="px-4 py-3">
                <TaskItemTravelProgressBar :taskItem="taskItem" />
              </div>
            </div>
          
          </template>
        </ItemPanel>
      </ScheduleTrackerItem>
    </ScheduleTracker>
  </div>
</template>

<style lang="scss" scoped>
.task-progress-panel {
  min-width: 1024px;
}
.task-progress-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.task-progress-row .col-number {
  width: 50px;
}
.task-progress-row .col-panel {
  flex: 1;
}

</style>

<script>
import _ from 'lodash'
import moment from 'moment'

import colourMixin from '@/mixins/colour'
import TaskManagerMixin from '@/modules/tasks/mixins/taskManagerMixin'
import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'

import ProgressTracker from '@/components/layout/progressTracker.vue'
import ProgressTrackerItem from '@/components/layout/progressTrackerItem.vue'
import ScheduleTracker from '@/components/layout/scheduleTracker.vue'
import ScheduleTrackerItem from '@/components/layout/scheduleTrackerItem.vue'

import TaskItemTravelProgressBar from '@/modules/tasks/components/taskItemTravelProgressBar.vue'

import { TaskItemStatusTypes, TaskItemTypes, TaskItemTypeLookup, TaskItemStatusTypeLookup, TaskSignoffTypes } from '@/store/types'

export default {
  name: 'TaskProgressPreview',
  mixins: [colourMixin, TaskManagerMixin, TaskDespatchMixin],
  components: {
    ProgressTracker,
    ProgressTrackerItem,
    ScheduleTracker,
    ScheduleTrackerItem,
    TaskItemTravelProgressBar
  },
  props: {
    task: Object,
    taskItemScannedItemList: Array
  },
  data() {
    return {
      taskList: []
    }
  },
  created() {
    this.TaskItemTypeLookup = TaskItemTypeLookup
    this.TaskItemStatusTypes = TaskItemStatusTypes
    this.TaskItemStatusTypeLookup = TaskItemStatusTypeLookup
  },
  mounted() {
  },
  beforeDestroy() {    
  },
  computed: {
    titleColumnWidth() {
      // Stage/Despatch columns are fixed width. We adjust the title column to take up the remaining space
      var stageColumnWidth = 10
      var startedColumnWidth = stageColumnWidth
      var completedColumnWidth = stageColumnWidth
      let stageCount = this.stagesUsed.length

      return (100 - (stageCount * stageColumnWidth + startedColumnWidth + completedColumnWidth)) + '%'
    }
  },
  methods: {
    hasLabels(labelList) {
      return (labelList && labelList.length > 0)
    },
    isTaskItemStoppageType(taskItem) {
      return taskItem.type == TaskItemTypes.stoppage
    },
    isTaskItemOrderDespatchType(taskItem) {
      return taskItem.type == TaskItemTypes.orderDespatch
    },
    isTaskItemLocationEnabled(taskItem) {
      return taskItem.isLocationEnabled
    },
    isTaskItemCompleted(taskItem) {
      return taskItem.status == TaskItemStatusTypes.completed
    },
    isTaskItemCancelled(taskItem) {
      return taskItem.status == TaskItemStatusTypes.cancelled
    },
    isTaskItemActive(taskItem) {
      return taskItem.status == TaskItemStatusTypes.active
    },
    calculateTaskItemProgressDescription(taskItem) {
      var description = ''

      if(taskItem.isResourceArrived)
        return `At ${taskItem.name}`

      description = `On the way to ${taskItem.name}`

      if(taskItem.estimatedArrivalDate && moment(taskItem.estimatedArrivalDate) > moment()) {
        var duration = ''
        var minutesDiff = moment.duration(moment(taskItem.estimatedArrivalDate).diff(moment())).asMinutes()

        // Round up in 5 minute blocks
        minutesDiff = Math.ceil(minutesDiff / 5) * 5

        if(minutesDiff > 60)
          duration = `over an hour`
        else if(minutesDiff == 60)
          duration = `an hour`
        else
          duration = `${minutesDiff} mins`
        
        description += `. Est. Arrival in ${duration}`
      }

      return description
    },
    contactSignoffDescription(taskItem) {
      switch(taskItem.contactSignoffType) {
        case TaskSignoffTypes.none:
          return 'None'
        case TaskSignoffTypes.signature:
          return `Signature By '${taskItem.contactSignoffName}'`
        case TaskSignoffTypes.photoProof:
          return `Photo Proof. Details: '${taskItem.contactSignoffPhotoDetails}'`
      }
      return 'None'
    },
  }
  
}
</script>